import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { bulkAPI, adminAPI } from '../../services/api'
import { bumpBusinessDay } from '../../utils/businessDays'
import ContactAvatar from '../shared/ContactAvatar'
import RatingIcon from '../shared/RatingIcon'
import { FLAG_OPTIONS } from '../../utils/flagOptions'

function renderFlag(bandera) {
  if (!bandera) return null
  const opt = FLAG_OPTIONS.find(o => o.key === bandera)
  return opt?.render ? opt.render('sm') : null
}

function dayLabel(dateStr) {
  if (!dateStr) return ''
  const [y, m, d] = dateStr.split('-').map(Number)
  const dow = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'][new Date(Date.UTC(y, m - 1, d)).getUTCDay()]
  return `${dow} ${String(d).padStart(2, '0')}`
}

function clampInt(v, lo, hi) {
  const n = parseInt(v, 10)
  if (!Number.isFinite(n)) return lo
  return Math.min(hi, Math.max(lo, n))
}

// Stepper compacto −N+ con número editable (tipear o clic). disabled => solo muestra el valor.
function Stepper({ label, hint, suffix, value, min, max, onChange, disabled }) {
  if (value == null) return null
  if (disabled) {
    return (
      <span className="inline-flex items-center gap-1">
        <span className="text-[11px] uppercase tracking-wide text-gray-400" title={hint}>{label}</span>
        <span className="text-sm font-medium text-gray-600 dark:text-gray-300">{value}{suffix ? ` ${suffix}` : ''}</span>
      </span>
    )
  }
  const set = v => onChange(clampInt(v, min, max))
  return (
    <span className="inline-flex items-center gap-1">
      <span className="text-[11px] uppercase tracking-wide text-gray-400" title={hint}>{label}</span>
      <span className="inline-flex items-center rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden">
        <button type="button" onClick={() => set(value - 1)} disabled={value <= min}
          className="px-1.5 py-0.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-surface-dark-hover disabled:opacity-30" aria-label={`bajar ${label}`}>−</button>
        <input type="text" inputMode="numeric" value={value}
          onChange={e => { const d = e.target.value.replace(/[^\d]/g, ''); if (d === '') return; set(d) }}
          className="w-9 text-center text-sm font-medium bg-transparent outline-none" aria-label={label} />
        <button type="button" onClick={() => set(value + 1)} disabled={value >= max}
          className="px-1.5 py-0.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-surface-dark-hover disabled:opacity-30" aria-label={`subir ${label}`}>+</button>
      </span>
      {suffix && <span className="text-[11px] text-gray-400">{suffix}</span>}
    </span>
  )
}

export default function SmartRescheduleModal({ contactIds, isAdmin = false, onClose, onApplied }) {
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState(null)
  const [preview, setPreview] = useState(null)
  const [assignments, setAssignments] = useState([]) // [{ contactId, date }]
  const [applying, setApplying] = useState(false)
  const [rate, setRate] = useState(null)       // repartir/día (tasa del lote; temporal, no persiste)
  const [cap, setCap] = useState(null)         // tope/día (cupo del agente)
  const [horizon, setHorizon] = useState(null) // horizonte de días (editable solo admin)
  const [saved, setSaved] = useState(false)
  const timerRef = useRef(null)
  const savedTimerRef = useRef(null)
  const removedRef = useRef(new Set()) // contactIds quitados a mano de la lista (persisten entre recalculos)

  const runPreview = useCallback((rt, c, h, refresh) => {
    // Solo los IDs vigentes (los quitados con ✕ salen del reparto) -> el backend
    // re-reparte a los restantes y rellena el cupo que libera el quitado.
    const activeIds = contactIds.filter(id => !removedRef.current.has(id))
    if (activeIds.length === 0) {
      setAssignments([])
      if (refresh) setRefreshing(false); else setLoading(false)
      return Promise.resolve(null)
    }
    if (refresh) setRefreshing(true); else setLoading(true)
    setError(null)
    const opts = {}
    if (rt != null) opts.perDayRate = rt
    if (c != null) opts.maxPerDay = c
    if (h != null) opts.horizonDays = h
    return bulkAPI.smartReschedulePreview(activeIds, opts)
      .then(r => {
        setPreview(r)
        const rem = removedRef.current // defensa anti-carrera: nunca revivir un quitado
        setAssignments(r.plan.filter(p => !rem.has(p.contactId)).map(p => ({ contactId: p.contactId, date: p.date })))
        return r
      })
      .catch(e => { setError(e?.response?.data?.error || 'Error generando el preview'); return null })
      .finally(() => { if (refresh) setRefreshing(false); else setLoading(false) })
  }, [contactIds])

  // Carga inicial: sin overrides; inicializa los steppers con la config real del agente.
  useEffect(() => {
    let alive = true
    runPreview(null, null, null, false).then(r => {
      if (alive && r) { setRate(r.perDayRate); setCap(r.maxPerDay); setHorizon(r.horizonDays) }
    })
    return () => { alive = false }
  }, [contactIds, runPreview])

  // Re-preview debounced; persiste config (tope/horizonte) solo si admin Y el cambio fue de config.
  function scheduleRecalc(rt, c, h, persist) {
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => {
      runPreview(rt, c, h, true)
      if (persist && isAdmin && preview?.agente) {
        adminAPI.saveRescheduleConfig([{ agente: preview.agente, maxPerDay: c, horizonDays: h }])
          .then(() => {
            setSaved(true)
            if (savedTimerRef.current) clearTimeout(savedTimerRef.current)
            savedTimerRef.current = setTimeout(() => setSaved(false), 1800)
          })
          .catch(() => {})
      }
    }, 350)
  }
  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current)
    if (savedTimerRef.current) clearTimeout(savedTimerRef.current)
  }, [])

  // Repartir/día: temporal (no persiste), lo puede mover cualquiera.
  function changeRate(v) { setRate(v); scheduleRecalc(v, cap, horizon, false) }
  // Tope/día: override del agente (temporal) o config persistida (admin). Acota la tasa si baja.
  function changeCap(v) {
    const nr = rate != null ? Math.min(rate, v) : v
    setRate(nr); setCap(v); setSaved(false)
    scheduleRecalc(nr, v, horizon, true)
  }
  // Horizonte: solo admin (persiste).
  function changeHorizon(v) { setHorizon(v); setSaved(false); scheduleRecalc(rate, cap, v, true) }

  // Detalle del prospecto por id (del preview); la lista se renderiza desde `assignments`
  // (en orden de ranking) para que quitar uno con la ✕ lo saque al toque sin re-repartir.
  const byId = useMemo(
    () => Object.fromEntries((preview?.plan || []).map(p => [p.contactId, p])),
    [preview]
  )

  // Cupo en vivo: usadosPrevios (del preview) + nuevos (de assignments) por día.
  const liveDays = useMemo(() => {
    if (!preview) return []
    const prev = Object.fromEntries(preview.daySummary.map(d => [d.date, d.usadosPrevios]))
    const real = Object.fromEntries(preview.daySummary.map(d => [d.date, d.ocupadoReal]))
    const placed = {}
    assignments.forEach(a => { placed[a.date] = (placed[a.date] || 0) + 1 })
    const dates = Array.from(new Set([...Object.keys(prev), ...Object.keys(placed)])).sort()
    return dates.map(date => {
      const previos = prev[date] || 0
      const nuevos = placed[date] || 0
      const usados = previos + nuevos
      return { date, cupo: preview.maxPerDay, previos, nuevos, usados, over: usados > preview.maxPerDay, ocupadoReal: real[date] || 0 }
    })
  }, [preview, assignments])

  function bump(contactId, dir) {
    setAssignments(prev => prev.map(a =>
      a.contactId === contactId ? { ...a, date: bumpBusinessDay(a.date, dir) } : a
    ))
  }

  // Quitar un prospecto de la lista de trabajo (seleccionado por error). Sale del apply
  // (queda con su fecha actual) y re-reparte a los restantes para que llenen el cupo que
  // libera (los que venían después suben). Feedback inmediato + re-reparto debounced.
  function removeRow(contactId) {
    removedRef.current.add(contactId)
    setAssignments(prev => prev.filter(a => a.contactId !== contactId))
    scheduleRecalc(rate, cap, horizon, false)
  }

  // Al cerrar (confirmar o cancelar) le pasamos al padre los quitados para que pode
  // la selección de la grilla (selectedIds menos los quitados en el modal).
  function handleClose() { onClose?.(Array.from(removedRef.current)) }

  async function confirm() {
    setApplying(true); setError(null)
    try {
      await bulkAPI.smartRescheduleApply(assignments)
      onApplied?.()
      handleClose()
    } catch (e) {
      setError(e?.response?.data?.error || 'Error aplicando el reagendamiento')
      setApplying(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-4 animate-fade-in" onClick={handleClose}>
      <div
        className="bg-white dark:bg-surface-dark w-full max-w-2xl rounded-xl shadow-xl max-h-[85vh] flex flex-col animate-modal-in"
        onClick={e => e.stopPropagation()}
      >
        {/* Header fijo: titulo + resumen + parametros + tira de cupos */}
        <div className="flex-shrink-0 p-5 pb-3">
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-base font-medium flex items-center gap-2">📅 Agendamiento inteligente</h3>
            <button onClick={handleClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">✕</button>
          </div>

          {preview && !loading && (
            <>
              <div className="text-sm text-gray-500 mb-2">
                {assignments.length} a reagendar · {preview.agente} · orden Mejor calificación
              </div>
              <div className="flex items-center gap-x-3 gap-y-1.5 flex-wrap mb-3">
                <Stepper label="Repartir/día" hint="Cuántos de los seleccionados colocar por día (acotado al cupo disponible)"
                  value={rate} min={1} max={cap || 30} onChange={changeRate} />
                <Stepper label="Tope/día" hint="Máx. rescatados que caben en el día (cupo del agente)"
                  value={cap} min={1} max={30} onChange={changeCap} />
                <Stepper label="Horizonte" suffix="días" hint="Hasta cuántos días hábiles adelante repartir"
                  value={horizon} min={1} max={60} onChange={changeHorizon} disabled={!isAdmin} />
                {refreshing && <span className="text-[11px] text-gray-400">actualizando…</span>}
                {isAdmin && saved && !refreshing && <span className="text-[11px] text-emerald-600">✓ guardado</span>}
              </div>

              {liveDays.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {liveDays.map(d => (
                    <div key={d.date} className="bg-gray-50 dark:bg-surface-dark-alt rounded-lg px-2.5 py-1.5 min-w-[78px]">
                      <div className="text-xs font-medium">{dayLabel(d.date)}</div>
                      <div className="flex gap-0.5 my-1">
                        {Array.from({ length: d.cupo }).map((_, i) => {
                          const esPrev = i < d.previos
                          const esNuevo = !esPrev && i < d.usados
                          return (
                            <span key={i} className={`flex-1 h-1.5 rounded-sm ${(!esPrev && !esNuevo) ? 'border border-gray-300 dark:border-gray-600' : ''}`}
                              style={esPrev ? { background: '#B4B2A9' } : esNuevo ? { background: '#1D9E75' } : undefined} />
                          )
                        })}
                      </div>
                      <div className={`text-[11px] ${d.over ? 'text-red-600 font-medium' : d.nuevos > 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-gray-500'}`}>
                        {d.nuevos > 0 ? `${d.previos}+${d.nuevos}` : d.usados}/{d.cupo}{d.over ? ' ⚠' : ''}
                      </div>
                      {d.ocupadoReal > 0 && (
                        <div className="text-[10px] text-gray-400 leading-tight" title="Rescatados que ese día ya tiene ahora (sin contar este reparto)">hay {d.ocupadoReal}</div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Cuerpo scrolleable: lista de prospectos */}
        <div className="flex-1 overflow-y-auto px-5">
          {loading && <div className="py-10 text-center text-sm text-gray-500">Calculando reparto…</div>}
          {error && !loading && <div className="py-3 text-sm text-red-600">{error}</div>}

          {preview && !loading && (
            <>
              <div className={`divide-y divide-gray-100 dark:divide-gray-700 transition-opacity duration-200 ${refreshing ? 'opacity-40 pointer-events-none' : ''}`}>
                {assignments.length === 0 && (
                  <div className="py-8 text-center text-sm text-gray-400">No quedan prospectos en la lista.</div>
                )}
                {assignments.map(a => {
                  const p = byId[a.contactId]
                  if (!p) return null
                  const date = a.date
                  return (
                    <div key={a.contactId} className="flex items-center gap-3 py-2 group">
                      <ContactAvatar
                        name={p.nombre}
                        seed={p.avatarSeed}
                        className="w-8 h-8 rounded-full text-xs flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="text-sm font-medium truncate">{p.nombre || '(sin nombre)'}</span>
                          <span className="flex-shrink-0">{renderFlag(p.bandera)}</span>
                          {p.iconNombre && <RatingIcon iconId={p.iconNombre} size="sm" className="flex-shrink-0" />}
                        </div>
                        <div className="text-[11px] text-gray-500 truncate" title={p.hint || ''}>
                          {p.hint || 'sin nota express'}
                        </div>
                      </div>
                      <div className="flex items-center gap-0.5 rounded-lg px-1 py-1 flex-shrink-0 bg-gray-50 dark:bg-surface-dark-alt border border-gray-200 dark:border-gray-600 transition-all duration-150 group-hover:border-gray-300 dark:group-hover:border-gray-500 hover:!border-primary-400 dark:hover:!border-primary-500 hover:bg-white dark:hover:bg-surface-dark hover:shadow-md">
                        <button onClick={() => bump(a.contactId, -1)} className="w-6 h-6 flex items-center justify-center rounded-md text-sm text-gray-500 hover:bg-primary-100 dark:hover:bg-primary-900/40 hover:text-primary-600 dark:hover:text-primary-300 active:scale-90 transition-all" aria-label="día anterior">‹</button>
                        <span className="text-xs font-semibold w-12 text-center text-gray-700 dark:text-gray-200">{dayLabel(date)}</span>
                        <button onClick={() => bump(a.contactId, 1)} className="w-6 h-6 flex items-center justify-center rounded-md text-sm text-gray-500 hover:bg-primary-100 dark:hover:bg-primary-900/40 hover:text-primary-600 dark:hover:text-primary-300 active:scale-90 transition-all" aria-label="día siguiente">›</button>
                      </div>
                      <button
                        onClick={() => removeRow(a.contactId)}
                        title="Quitar de la lista"
                        aria-label="Quitar de la lista"
                        className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-md text-gray-300 dark:text-gray-600 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 active:scale-90 transition-all"
                      >✕</button>
                    </div>
                  )
                })}
              </div>

              {preview.overflow.length > 0 && (
                <div className="mt-3 mb-1 p-2 bg-amber-50 dark:bg-amber-900/20 rounded-lg text-xs text-amber-800 dark:text-amber-300">
                  No caben en el horizonte ({preview.overflow.length}): {preview.overflow.map(o => o.nombre).join(', ')}. Quedan con su fecha actual.
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer fijo: botones */}
        {preview && !loading && (
          <div className="flex-shrink-0 flex items-center justify-end gap-2 p-5 pt-3 border-t border-gray-100 dark:border-gray-700">
            <button onClick={handleClose} className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-surface-dark-hover transition-all active:scale-95">Cancelar</button>
            <button
              onClick={confirm}
              disabled={applying || assignments.length === 0}
              className="px-3.5 py-1.5 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 hover:shadow-md disabled:opacity-50 disabled:hover:shadow-none transition-all active:scale-95 disabled:active:scale-100"
            >
              {applying ? 'Aplicando…' : `Confirmar reparto (${assignments.length})`}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
